import { useEffect, useState, useCallback } from "react";
import { http, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import BookingForm from "@/pages/admin/booking/BookingForm";
import BookingDetail from "@/pages/admin/booking/BookingDetail";
import BookingList from "@/pages/admin/booking/BookingList";

const EMPTY_FORM = {
  full_name: "", mobile: "", email: "",
  check_in_date: "", check_out_date: "", adults: 2, children: 0,
  room_ids: [], booking_amount: "", advance_amount: "",
  payment_method: "UPI", payment_reference: "", payment_date: "", notes: "",
  responsible_manager_id: "",
};

export default function BookingManager({ triggerNew = 0 }) {
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [managers, setManagers] = useState([]);
  const [view, setView] = useState("list");
  const [form, setForm] = useState(EMPTY_FORM);
  const [pending, setPending] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "UPI", reference: "" });

  const load = useCallback(async () => {
    const [b, r, m] = await Promise.all([http.get("/admin/bookings"), http.get("/admin/rooms"), http.get("/admin/staff/managers")]);
    setBookings(b.data.bookings);
    setRooms(r.data.rooms);
    setManagers(m.data.managers || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (triggerNew > 0) { setForm(EMPTY_FORM); setPending(null); setView("new"); } }, [triggerNew]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const nights = form.check_in_date && form.check_out_date
    ? Math.max(0, Math.round((new Date(form.check_out_date) - new Date(form.check_in_date)) / 864e5)) : 0;
  const balance = (parseFloat(form.booking_amount) || 0) - (parseFloat(form.advance_amount) || 0);
  const emailOk = !form.email || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email);
  const formValid = form.full_name && form.mobile && form.check_in_date && form.check_out_date &&
    form.adults >= 1 && form.room_ids.length > 0 && form.booking_amount !== "" && form.advance_amount !== "" && emailOk;

  const payload = () => ({
    full_name: form.full_name, mobile: form.mobile, email: form.email || undefined,
    check_in_date: form.check_in_date, check_out_date: form.check_out_date,
    adults: parseInt(form.adults) || 1, children: parseInt(form.children) || 0,
    room_ids: form.room_ids, booking_amount: parseFloat(form.booking_amount) || 0,
    advance_amount: parseFloat(form.advance_amount) || 0,
    payment_method: form.payment_method, payment_reference: form.payment_reference,
    payment_date: form.payment_date, notes: form.notes,
    responsible_manager_id: form.responsible_manager_id || undefined,
  });

  const createDraft = async () => {
    const r = await http.post("/admin/bookings", payload());
    return r.data;
  };

  const saveDraft = async () => {
    if (!formValid) return toast.error("Fill all required fields (valid email if provided)");
    setBusy(true);
    try {
      const b = await createDraft();
      toast.success(`Draft saved · ${b.booking_reference}`);
      setView("list"); setForm(EMPTY_FORM); load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const confirmSend = async () => {
    setBusy(true);
    try {
      const b = pending || await createDraft();
      const r = await http.post(`/admin/bookings/${b.id}/confirm`);
      if (r.data.conflict) {
        toast.error(`ROOM NOT AVAILABLE — conflicts with ${r.data.conflicting_bookings.map((c) => c.booking_reference).join(", ")}`);
        return;
      }
      toast.success(`Booking ${r.data.booking.booking_reference} CONFIRMED`);
      setPending(null); setForm(EMPTY_FORM);
      await openDetail(r.data.booking.id);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const openDetail = async (id) => {
    const r = await http.get(`/admin/bookings/${id}`);
    setDetail(r.data);
    setView("detail");
  };
  const reloadDetail = async () => { if (detail) await openDetail(detail.id); load(); };

  const addPayment = async () => {
    if (!payForm.amount) return;
    try {
      await http.post(`/admin/bookings/${detail.id}/payments`, { amount: parseFloat(payForm.amount), method: payForm.method, reference: payForm.reference });
      toast.success("Payment recorded (pending verification)");
      setPayForm({ amount: "", method: "UPI", reference: "" });
      reloadDetail();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const verifyPayment = async (pid) => {
    try { await http.post(`/admin/booking-payments/${pid}/verify`); toast.success("Payment verified"); reloadDetail(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const resend = async (channel) => {
    try { const r = await http.post(`/admin/bookings/${detail.id}/resend`, { channel }); toast.info(r.data.reason); reloadDetail(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const markSync = async (platform) => {
    try { await http.post(`/admin/bookings/${detail.id}/sync-mark`, { platform }); toast.success(`${platform === "airbnb" ? "Airbnb" : "Booking.com"} marked manually blocked`); reloadDetail(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const setConfStatus = async (status) => {
    try { await http.post(`/admin/bookings/${detail.id}/confirmation-status`, { status }); toast.success("Confirmation status saved"); reloadDetail(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const cancelBooking = async (reason) => {
    try {
      await http.post(`/admin/bookings/${detail.id}/cancel`, { reason });
      toast.success("Booking cancelled — rooms released for those dates");
      reloadDetail();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deleteBooking = async () => {
    try {
      await http.delete(`/admin/bookings/${detail.id}`);
      toast.success("Test booking permanently deleted");
      setDetail(null); setView("list"); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const editBooking = async (payload) => {
    try {
      await http.put(`/admin/bookings/${detail.id}`, payload);
      toast.success("Booking updated");
      reloadDetail();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const copyMessage = async () => {
    const d = detail;
    const msg = [
      "FREEBIRD NEST",
      "Booking Confirmed ✓",
      "",
      `Hello ${d.full_name.split(" ")[0]},`,
      "Your booking at Freebird Nest is confirmed.",
      "",
      `Booking ID: ${d.booking_reference}`,
      `Check-In: ${d.check_in_date}`,
      `Check-Out: ${d.check_out_date}`,
      `Guests: ${d.total_pax}`,
      `Room: ${d.rooms.map((r) => r.name).join(", ")}`,
      `Booking Amount: ₹${Number(d.booking_amount).toLocaleString("en-IN")}`,
      `Payment Received: ₹${Number(d.verified_paid).toLocaleString("en-IN")}`,
      `Balance Due: ₹${Number(d.balance_remaining).toLocaleString("en-IN")}`,
      "",
      "Thank you for choosing Freebird Nest.",
      "We look forward to welcoming you to the mountains.",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(msg);
      toast.success("Confirmation message copied — paste it in WhatsApp or email");
    } catch {
      window.prompt("Copy this message:", msg);
    }
  };

  const startNew = () => { setForm(EMPTY_FORM); setPending(null); setView("new"); };
  const goReview = () => (formValid ? setView("review") : toast.error("Fill all required fields"));

  if (view === "new" || view === "review") {
    return (
      <BookingForm
        view={view} form={form} setField={set} rooms={rooms} managers={managers} nights={nights} balance={balance} emailOk={emailOk} busy={busy}
        onBack={() => setView("list")} onSaveDraft={saveDraft} onReview={goReview} onEdit={() => setView("new")} onConfirm={confirmSend}
      />
    );
  }

  if (view === "detail" && detail) {
    return (
      <BookingDetail
        detail={detail} payForm={payForm} setPayForm={setPayForm} rooms={rooms}
        onBack={() => setView("list")} onAddPayment={addPayment} onVerifyPayment={verifyPayment}
        onResend={resend} onMarkSync={markSync} onSetConfStatus={setConfStatus} onCopyMessage={copyMessage}
        onCancel={cancelBooking} onDelete={deleteBooking} onEdit={editBooking} onChanged={reloadDetail}
      />
    );
  }

  return <BookingList bookings={bookings} onOpen={openDetail} onNew={startNew} />;
}
